import React, { useState, useMemo, useEffect } from 'react';
import {
    FaCheckCircle
} from 'react-icons/fa';
import styles from '../ListingScore.module.css';

const ActionPlanSection = ({ productData, showDetailedAnalysis }) => {
    const safeArray = (arr) => Array.isArray(arr) ? arr : [];
    const recommendations = productData && productData.analysis ? safeArray(productData.analysis.recommendations) : [];
    
    const formatComponentName = (component) => {
        const nameMap = {
            'title': 'Title Optimization',
            'description': 'Description Quality',
            'a_plus_content': 'A+ Content',
            'bullet_points': 'Bullet Points',
            'b_plus_images': 'B+ Images',
            'images': 'B+ Images',
            'additional_content': 'Additional Content'
        };
        return nameMap[component] || component.charAt(0).toUpperCase() + component.slice(1);
    };

    // Group recommendations by component
    const groupedRecommendations = useMemo(() => {
        const grouped = {};
        recommendations.forEach(rec => {
            const component = rec.component || 'additional_content';
            const tabName = formatComponentName(component);
            if (!grouped[tabName]) {
                grouped[tabName] = [];
            }
            grouped[tabName].push(rec);
        });
        // Sort each group by priority
        Object.keys(grouped).forEach(key => {
            grouped[key].sort((a, b) => (a.priority || 0) - (b.priority || 0));
        });
        return grouped;
    }, [recommendations]);

    const tabNames = Object.keys(groupedRecommendations);
    const [selectedTab, setSelectedTab] = useState(null);

    // Set first tab as default when tabs are available
    useEffect(() => {
        if (tabNames.length > 0 && !selectedTab) {
            setSelectedTab(tabNames[0]);
        } else if (selectedTab && !tabNames.includes(selectedTab) && tabNames.length > 0) {
            // If selected tab no longer exists, select first available
            setSelectedTab(tabNames[0]);
        }
    }, [tabNames, selectedTab]);

    // Get the first recommendation for the selected tab (or use the first one if multiple)
    const selectedRecommendation = selectedTab && groupedRecommendations[selectedTab] 
        ? groupedRecommendations[selectedTab][0] 
        : null;

    // Get expected impact text - try to extract from expected_impact or create from impact
    const getExpectedImpactText = (rec) => {
        if (rec.expected_impact) {
            return rec.expected_impact;
        }
        // Try to extract impact info from other fields or create default
        if (rec.impact === 'high') {
            return '+15–20% CTR, +1.0 score';
        } else if (rec.impact === 'medium') {
            return '+10–15% CTR, +0.5 score';
        } else {
            return '+5–10% CTR, +0.3 score';
        }
    };

    if (recommendations.length === 0) {
        return (
            <div className={styles.actionPlanBox}>
                <div className={styles.noRecommendations}>
                    <FaCheckCircle className={styles.successIcon} />
                    <h3>Excellent Work!</h3>
                    <p>Your listing is performing well across all components. No critical recommendations at this time.</p>
                </div>
            </div>
        );
    }

    return (
        <div className={styles.newActionPlanContainer}>
            {/* Header */}
            <div className={styles.newActionPlanHeader}>
                <div className={styles.newActionPlanTitleBadge}>
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <g opacity="0.7">
                <path d="M11.6985 9.62219C7.84504 8.752 7.24815 8.1551 6.37799 4.30155C6.33812 4.12542 6.18135 4 6.00019 4C5.81904 4 5.66227 4.12542 5.6224 4.30155C4.75185 8.1551 4.15535 8.752 0.301926 9.62219C0.125415 9.66245 0 9.81884 0 10C0 10.1812 0.125415 10.3375 0.301926 10.3778C4.15535 11.2484 4.75185 11.8453 5.6224 15.6985C5.66227 15.8746 5.81904 16 6.00019 16C6.18135 16 6.33812 15.8746 6.37799 15.6985C7.24854 11.8453 7.84504 11.2484 11.6985 10.3778C11.875 10.3375 12 10.1812 12 10C12 9.81884 11.8746 9.66245 11.6985 9.62219Z" fill="url(#paint0_linear_399_3026)"/>
                <path d="M15.6965 3.11959C13.635 2.65407 13.3455 2.36463 12.88 0.303467C12.8395 0.125828 12.6821 0 12.4998 0C12.3175 0 12.1601 0.125828 12.1196 0.303467C11.6541 2.36463 11.3646 2.65407 9.30347 3.11959C9.12583 3.16011 9 3.31749 9 3.49981C9 3.68212 9.12583 3.8395 9.30347 3.88002C11.3646 4.34554 11.6541 4.63498 12.1196 6.69653C12.1601 6.87378 12.3175 7 12.4998 7C12.6821 7 12.8395 6.87378 12.88 6.69653C13.3455 4.63498 13.635 4.34554 15.6965 3.88002C15.8738 3.8395 16 3.68212 16 3.49981C16 3.31749 15.8738 3.16011 15.6965 3.11959Z" fill="url(#paint1_linear_399_3026)"/>
                </g>
                <defs>
                <linearGradient id="paint0_linear_399_3026" x1="-4.47035e-08" y1="10" x2="11.0143" y2="16" gradientUnits="userSpaceOnUse">
                <stop stopColor="#2766AA"/>
                <stop offset="1" stopColor="#0752B0"/>
                </linearGradient>
                <linearGradient id="paint1_linear_399_3026" x1="9" y1="3.5" x2="15.425" y2="7" gradientUnits="userSpaceOnUse">
                <stop stopColor="#2766AA"/>
                <stop offset="1" stopColor="#0752B0"/>
                </linearGradient>
                </defs>
                </svg>
                    <div className={styles.newActionPlanTitleText}>Action Points for you</div>
                </div>
            </div>

            {/* Tabs */}
            <div className={styles.newActionPlanTabs}>
                {tabNames.map((tabName) => (
                    <button
                        key={tabName}
                        className={`${styles.newActionPlanTab} ${selectedTab === tabName ? styles.newActionPlanTabActive : ''}`}
                        onClick={() => setSelectedTab(tabName)}
                    >
                        {selectedTab === tabName && <div className={styles.newActionPlanTabIndicator}></div>}
                        <span className={selectedTab === tabName ? styles.newActionPlanTabTextActive : styles.newActionPlanTabText}>
                            {tabName}
                        </span>
                    </button>
                ))}
            </div>

            {/* Content */}
            {selectedRecommendation && (
                <div className={styles.newActionPlanContent}>
                    {/* Recommendation Title Block */}
                    <div className={styles.newActionPlanTitleBlock}>
                        <div className={styles.newActionPlanTitleTextBlock}>
                            {selectedRecommendation.recommendation}
                        </div>
                        {selectedRecommendation.issue && (
                            <div className={styles.newActionPlanImpactBadge}>
                                <span>{selectedRecommendation.issue.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</span>
                            </div>
                        )}
                    </div>

                    {/* Three Cards */}
                    <div className={styles.newActionPlanCards}>
                        {/* Why This Matters */}
                        {selectedRecommendation.detailed_justification && (
                            <div className={styles.newActionPlanCard}>
                                <div className={styles.newActionPlanCardContent}>
                                    <div className={styles.newActionPlanCardHeader}>
                                    <svg width="36" height="36" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <rect x="0.5" y="0.5" width="35" height="35" rx="5.5" stroke="#78B3F3"/>
                                    <path fillRule="evenodd" clipRule="evenodd" d="M10.6316 9.00883C9.75923 9.00883 9.05263 9.68603 9.05263 10.5221V23.8891C9.05263 24.7251 9.75923 25.4023 10.6316 25.4023H16.5698L21.2711 17.5989C21.8527 16.6342 23.1395 16.3026 24.1474 16.86C25.154 17.4173 25.4987 18.6519 24.9172 19.6166L21.4315 25.4023H25.3685C26.2409 25.4023 26.9475 24.7251 26.9475 23.8891V10.5221C26.9475 9.68603 26.2409 9.00883 25.3685 9.00883H10.6316ZM20.8237 26.4111H25.3684C26.8224 26.4111 28 25.2825 28 23.8891V10.5221C28 9.12864 26.8224 8 25.3684 8H10.6316C9.17763 8 8 9.12864 8 10.5221V23.8891C8 25.2825 9.17763 26.4111 10.6316 26.4111H15.9618L14.9473 28.0972C14.8447 28.2662 14.8157 28.4666 14.8644 28.6558L15.3184 30.4238C15.425 30.8412 15.8736 31.0896 16.3039 30.9698L18.1289 30.4629C18.325 30.4087 18.4907 30.2838 18.5934 30.1149L20.8237 26.4111ZM12.4738 12.7917C12.4738 12.513 12.7093 12.2873 13.0001 12.2873H22.7368C23.0276 12.2873 23.2632 12.513 23.2632 12.7917C23.2632 13.0704 23.0276 13.2961 22.7368 13.2961H13.0001C12.7093 13.2961 12.4738 13.0704 12.4738 12.7917ZM12.4738 17.0792C12.4738 16.8005 12.7093 16.5748 13.0001 16.5748H18.2632C18.554 16.5748 18.7896 16.8005 18.7896 17.0792C18.7896 17.3579 18.554 17.5836 18.2632 17.5836H13.0001C12.7093 17.5836 12.4738 17.3579 12.4738 17.0792ZM13.0001 21.1145C12.7093 21.1145 12.4738 21.3402 12.4738 21.6189C12.4738 21.8976 12.7093 22.1234 13.0001 22.1234H16.158C16.4488 22.1234 16.6843 21.8976 16.6843 21.6189C16.6843 21.3402 16.4488 21.1145 16.158 21.1145H13.0001ZM23.6211 17.7336C24.1237 18.0122 24.2961 18.6289 24.0053 19.1119L17.7343 29.5217L16.2751 29.9278L15.9119 28.5129L22.1829 18.103C22.4737 17.62 23.1171 17.4549 23.6211 17.7336Z" fill="#78B3F3"/>
                                    </svg>
                                        <div className={styles.newActionPlanCardTitle}>Why This Matters</div>
                                    </div>
                                    <div className={styles.newActionPlanCardText}>
                                        {selectedRecommendation.detailed_justification}
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Expected Impact */}
                        {selectedRecommendation.expected_impact && (
                            <div className={styles.newActionPlanCard}>
                                <div className={styles.newActionPlanCardContent}>
                                    <div className={styles.newActionPlanCardHeader}>
                                    <svg width="36" height="36" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <rect x="0.5" y="0.5" width="35" height="35" rx="5.5" stroke="#78B3F3"/>
                                    <path fillRule="evenodd" clipRule="evenodd" d="M10.6316 9.00883C9.75923 9.00883 9.05263 9.68603 9.05263 10.5221V23.8891C9.05263 24.7251 9.75923 25.4023 10.6316 25.4023H16.5698L21.2711 17.5989C21.8527 16.6342 23.1395 16.3026 24.1474 16.86C25.154 17.4173 25.4987 18.6519 24.9172 19.6166L21.4315 25.4023H25.3685C26.2409 25.4023 26.9475 24.7251 26.9475 23.8891V10.5221C26.9475 9.68603 26.2409 9.00883 25.3685 9.00883H10.6316ZM20.8237 26.4111H25.3684C26.8224 26.4111 28 25.2825 28 23.8891V10.5221C28 9.12864 26.8224 8 25.3684 8H10.6316C9.17763 8 8 9.12864 8 10.5221V23.8891C8 25.2825 9.17763 26.4111 10.6316 26.4111H15.9618L14.9473 28.0972C14.8447 28.2662 14.8157 28.4666 14.8644 28.6558L15.3184 30.4238C15.425 30.8412 15.8736 31.0896 16.3039 30.9698L18.1289 30.4629C18.325 30.4087 18.4907 30.2838 18.5934 30.1149L20.8237 26.4111ZM12.4738 12.7917C12.4738 12.513 12.7093 12.2873 13.0001 12.2873H22.7368C23.0276 12.2873 23.2632 12.513 23.2632 12.7917C23.2632 13.0704 23.0276 13.2961 22.7368 13.2961H13.0001C12.7093 13.2961 12.4738 13.0704 12.4738 12.7917ZM12.4738 17.0792C12.4738 16.8005 12.7093 16.5748 13.0001 16.5748H18.2632C18.554 16.5748 18.7896 16.8005 18.7896 17.0792C18.7896 17.3579 18.554 17.5836 18.2632 17.5836H13.0001C12.7093 17.5836 12.4738 17.3579 12.4738 17.0792ZM13.0001 21.1145C12.7093 21.1145 12.4738 21.3402 12.4738 21.6189C12.4738 21.8976 12.7093 22.1234 13.0001 22.1234H16.158C16.4488 22.1234 16.6843 21.8976 16.6843 21.6189C16.6843 21.3402 16.4488 21.1145 16.158 21.1145H13.0001ZM23.6211 17.7336C24.1237 18.0122 24.2961 18.6289 24.0053 19.1119L17.7343 29.5217L16.2751 29.9278L15.9119 28.5129L22.1829 18.103C22.4737 17.62 23.1171 17.4549 23.6211 17.7336Z" fill="#78B3F3"/>
                                    </svg>
                                        <div className={styles.newActionPlanCardTitle}>Expected Impact</div>
                                    </div>
                                    <div className={styles.newActionPlanCardText}>
                                        {selectedRecommendation.expected_impact}
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Implementation Guide */}
                        {selectedRecommendation.implementation_steps && (
                            <div className={styles.newActionPlanCard}>
                                <div className={styles.newActionPlanCardContent}>
                                    <div className={styles.newActionPlanCardHeader}>
                                    <svg width="36" height="36" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <rect x="0.5" y="0.5" width="35" height="35" rx="5.5" stroke="#78B3F3"/>
                                    <path fillRule="evenodd" clipRule="evenodd" d="M10.6316 9.00883C9.75923 9.00883 9.05263 9.68603 9.05263 10.5221V23.8891C9.05263 24.7251 9.75923 25.4023 10.6316 25.4023H16.5698L21.2711 17.5989C21.8527 16.6342 23.1395 16.3026 24.1474 16.86C25.154 17.4173 25.4987 18.6519 24.9172 19.6166L21.4315 25.4023H25.3685C26.2409 25.4023 26.9475 24.7251 26.9475 23.8891V10.5221C26.9475 9.68603 26.2409 9.00883 25.3685 9.00883H10.6316ZM20.8237 26.4111H25.3684C26.8224 26.4111 28 25.2825 28 23.8891V10.5221C28 9.12864 26.8224 8 25.3684 8H10.6316C9.17763 8 8 9.12864 8 10.5221V23.8891C8 25.2825 9.17763 26.4111 10.6316 26.4111H15.9618L14.9473 28.0972C14.8447 28.2662 14.8157 28.4666 14.8644 28.6558L15.3184 30.4238C15.425 30.8412 15.8736 31.0896 16.3039 30.9698L18.1289 30.4629C18.325 30.4087 18.4907 30.2838 18.5934 30.1149L20.8237 26.4111ZM12.4738 12.7917C12.4738 12.513 12.7093 12.2873 13.0001 12.2873H22.7368C23.0276 12.2873 23.2632 12.513 23.2632 12.7917C23.2632 13.0704 23.0276 13.2961 22.7368 13.2961H13.0001C12.7093 13.2961 12.4738 13.0704 12.4738 12.7917ZM12.4738 17.0792C12.4738 16.8005 12.7093 16.5748 13.0001 16.5748H18.2632C18.554 16.5748 18.7896 16.8005 18.7896 17.0792C18.7896 17.3579 18.554 17.5836 18.2632 17.5836H13.0001C12.7093 17.5836 12.4738 17.3579 12.4738 17.0792ZM13.0001 21.1145C12.7093 21.1145 12.4738 21.3402 12.4738 21.6189C12.4738 21.8976 12.7093 22.1234 13.0001 22.1234H16.158C16.4488 22.1234 16.6843 21.8976 16.6843 21.6189C16.6843 21.3402 16.4488 21.1145 16.158 21.1145H13.0001ZM23.6211 17.7336C24.1237 18.0122 24.2961 18.6289 24.0053 19.1119L17.7343 29.5217L16.2751 29.9278L15.9119 28.5129L22.1829 18.103C22.4737 17.62 23.1171 17.4549 23.6211 17.7336Z" fill="#78B3F3"/>
                                    </svg>
                                        <div className={styles.newActionPlanCardTitle}>Implementation Guide</div>
                                    </div>
                                    <div className={styles.newActionPlanCardText}>
                                        {selectedRecommendation.implementation_steps.split(/(\d+\.\s)/).map((part, i) =>
                                            /^\d+\.\s$/.test(part) ? <strong key={i}>{part}</strong> : part
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default ActionPlanSection;
